import { useState, useCallback, useRef } from 'react'
import { PageHeader, DataTable, StatusBadge, ColumnSettingsButton, colors, btnPrimary } from '../../components/ui'
import type { Column } from '../../components/ui'
import { getAuthToken } from '../../services/api'
import { useApiList } from '../../hooks/use-api-list'
import { type EnvironmentRow } from '../fleet/fleet-helpers'
import { useTableColumns } from '../../hooks/use-table-columns'

type TunnelState = 'idle' | 'connecting' | 'connected' | 'timeout' | 'error'

interface SessionState {
  readonly loading: boolean
  readonly error: string | null
  readonly loadingEnvId: string | null
  readonly tunnelState: TunnelState
}

const POLL_INTERVAL_MS = 3000
const POLL_TIMEOUT_MS = 90_000

export function RemoteAccessPage() {
  const [sessionState, setSessionState] = useState<SessionState>({
    loading: false,
    error: null,
    loadingEnvId: null,
    tunnelState: 'idle',
  })

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { data: environments, loading: envsLoading } = useApiList<EnvironmentRow>({
    path: '/fleet/environments',
  })
  const { tableId: _, ...tableColumnProps } = useTableColumns('remote-access')

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  const handleOpen = useCallback(
    async (envId: string) => {
      setSessionState({ loading: true, error: null, loadingEnvId: envId, tunnelState: 'connecting' })

      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthToken()}`,
      }

      try {
        const reqRes = await fetch(`/api/environments/${envId}/remote-access/request`, {
          method: 'POST',
          headers,
        })
        if (!reqRes.ok) {
          const err = (await reqRes.json().catch(() => ({}))) as { error?: string }
          throw new Error(err.error ?? `Request failed (${reqRes.status})`)
        }

        const startedAt = Date.now()
        await new Promise<void>((resolve, reject) => {
          pollRef.current = setInterval(async () => {
            if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
              stopPolling()
              reject(new Error('timeout'))
              return
            }
            try {
              const statusRes = await fetch(`/api/environments/${envId}/remote-access/status`, { headers })
              if (statusRes.ok) {
                const body = (await statusRes.json()) as { data?: { connectedToRelay?: boolean } }
                if (body.data?.connectedToRelay) {
                  stopPolling()
                  resolve()
                }
              }
            } catch {
              // Retry on next tick
            }
          }, POLL_INTERVAL_MS)
        })

        setSessionState((s) => ({ ...s, tunnelState: 'connected' }))

        const sessionRes = await fetch('/api/remote-access/sessions', {
          method: 'POST',
          headers,
          body: JSON.stringify({ environmentId: envId }),
        })
        if (!sessionRes.ok) {
          const err = (await sessionRes.json().catch(() => ({}))) as { error?: string }
          throw new Error(err.error ?? `Session failed (${sessionRes.status})`)
        }
        const body = (await sessionRes.json()) as { data: { token: string; relayUrl: string } }
        const url = `${body.data.relayUrl}/env/${envId}/?token=${encodeURIComponent(body.data.token)}`
        window.location.assign(url)
      } catch (err) {
        stopPolling()
        const message = err instanceof Error ? err.message : 'Failed to open session'
        setSessionState({
          loading: false,
          error:
            message === 'timeout'
              ? 'Edge did not connect. It may be offline or the heartbeat interval is long. Try again in a minute.'
              : message,
          loadingEnvId: null,
          tunnelState: message === 'timeout' ? 'timeout' : 'error',
        })
      }
    },
    [stopPolling],
  )

  const handleDisconnect = useCallback(async (envId: string) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAuthToken()}`,
    }
    await fetch(`/api/environments/${envId}/remote-access/release`, { method: 'POST', headers }).catch(() => {})
    setSessionState({ loading: false, error: null, loadingEnvId: null, tunnelState: 'idle' })
  }, [])

  const columns: readonly Column<EnvironmentRow>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (row) => row.name,
    },
    {
      key: 'org',
      header: 'Organization',
      sortable: true,
      render: (row) => row.ownerOrganizationName ?? '—',
    },
    // Note: remote_access entitlement is a per-tenancy license feature now
    // (the Environment no longer carries license features), and on Central the
    // remote-access action isn't license-gated, so there's no env-level
    // "enabled" status to show here. Per-tenancy gating arrives with Phase 2.
    {
      key: 'tunnel',
      header: 'Tunnel',
      render: (row) => {
        if (sessionState.loadingEnvId === row.id && sessionState.tunnelState === 'connecting') {
          return <StatusBadge status="warning" label="Connecting…" />
        }
        if (sessionState.loadingEnvId === row.id && sessionState.tunnelState === 'connected') {
          return <StatusBadge status="active" label="Connected" />
        }
        return <StatusBadge status="inactive" label="Offline" />
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => {
        const isLoading = sessionState.loading && sessionState.loadingEnvId === row.id
        const isConnected = sessionState.loadingEnvId === row.id && sessionState.tunnelState === 'connected'
        return (
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              style={{ ...btnPrimary, padding: '4px 10px', fontSize: '0.8125rem' }}
              onClick={(e) => {
                e.stopPropagation()
                void handleOpen(row.id)
              }}
              disabled={sessionState.loading}
            >
              {isLoading ? 'Connecting…' : 'Open'}
            </button>
            {isConnected && (
              <button
                style={{
                  padding: '4px 10px',
                  fontSize: '0.8125rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  background: 'white',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  void handleDisconnect(row.id)
                }}
              >
                Disconnect
              </button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <div>
      <PageHeader
        title="Remote Access"
        subtitle="Open a secure session to an edge deployment"
        actions={<ColumnSettingsButton columns={columns} {...tableColumnProps} />}
      />
      <div style={{ padding: '24px' }}>
        {sessionState.error && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '0.875rem',
            }}
          >
            {sessionState.error}
          </div>
        )}

        {envsLoading ? (
          <p style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Loading environments...</p>
        ) : (
          <DataTable
            columns={columns}
            rows={environments}
            keyField="id"
            emptyMessage="No deployments found. Enroll an edge deployment using the fleet management portal."
            {...tableColumnProps}
          />
        )}
      </div>
    </div>
  )
}
